// AI-egress classification gate (ADR-005). Boundary module — no Office.js
// dependency, fully unit-testable in isolation. Decides whether an email is
// safe to send to the configured AI endpoint, based on its Microsoft Purview
// sensitivity label (when readable) and, as a fallback, a client-side content
// heuristic. See assertSafeToSendToAI for the orchestration used by callers.

import type { MailboxAdapter, EmailSummary } from "./mailbox-adapter";
import type { AddinSettings } from "./settings";
import type { OrgPolicy } from "./policy";

export type SensitivityLevel = "public" | "internal" | "confidential" | "secret";

/** Graduated scale, least to most sensitive — index order is what a threshold
 * comparison relies on. */
export const SENSITIVITY_LEVEL_ORDER: SensitivityLevel[] = [
  "public",
  "internal",
  "confidential",
  "secret",
];

export const DEFAULT_BLOCK_THRESHOLD: SensitivityLevel = "secret";

export interface LabelClassification {
  level: SensitivityLevel | "unknown";
  labelName?: string;
  source: "graph-label" | "none";
}

export interface ContentScanResult {
  hit: boolean;
  ruleId?: string;
}

export type BlockReason = "label" | "content-heuristic";

/** Thrown by `assertSafeToSendToAI` to stop a prompt from being built. Carries
 * only a reason code and (for the label case) the label name — never the
 * matched sensitive content itself, so callers can log/report safely. */
export class BlockedByClassificationError extends Error {
  constructor(
    public readonly reason: BlockReason,
    public readonly detail?: string
  ) {
    super(
      reason === "label"
        ? "This email's sensitivity label does not allow AI processing."
        : "This email's content matched a sensitive-data pattern and cannot be sent to the AI assistant."
    );
    // Restores the prototype chain lost when TS downlevels classes extending
    // built-in Error to `target: "es5"` (production ts-loader build) —
    // without this, `instanceof BlockedByClassificationError` silently
    // returns false in dist/.
    Object.setPrototypeOf(this, BlockedByClassificationError.prototype);
    this.name = "BlockedByClassificationError";
  }
}

function normalize(name: string): string {
  return name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

// Checked most-severe-first: a name containing "confidentiel" AND "secret"
// (e.g. "Hautement confidentiel secret") must resolve to "secret", not stop
// at the first substring found in scan order.
const DEFAULT_LEVEL_KEYWORDS: ReadonlyArray<readonly [SensitivityLevel, readonly string[]]> = [
  [
    "secret",
    [
      "secret",
      "top secret",
      "hautement confidentiel",
      "highly confidential",
      "strictement confidentiel",
    ],
  ],
  ["confidential", ["confidentiel", "confidential"]],
  ["internal", ["interne", "internal", "general", "général"]],
  ["public", ["public"]],
];

/**
 * Resolves a detected Purview sensitivity-label display name to a graduated
 * level. `labelName` is `null` when no label could be read at all (no
 * Graph/NAA, read failure, or none applied) — always resolves to `"unknown"`.
 * `labelMapOverrides` (org policy, exact match after normalization) takes
 * priority over the built-in FR/EN keyword mapping. A name matching neither
 * also resolves to `"unknown"` rather than being guessed at.
 */
export function classifyLabel(
  labelName: string | null,
  labelMapOverrides: Record<string, SensitivityLevel> = {}
): LabelClassification {
  if (!labelName) {
    return { level: "unknown", source: "none" };
  }

  const normalized = normalize(labelName);

  const overrideEntry = Object.entries(labelMapOverrides).find(
    ([name]) => normalize(name) === normalized
  );
  if (overrideEntry) {
    return { level: overrideEntry[1], labelName, source: "graph-label" };
  }

  for (const [level, keywords] of DEFAULT_LEVEL_KEYWORDS) {
    if (keywords.some((keyword) => normalized.includes(keyword))) {
      return { level, labelName, source: "graph-label" };
    }
  }

  return { level: "unknown", labelName, source: "graph-label" };
}

/** True when `level` is at or above `threshold` on the graduated scale. */
export function isAtOrAboveThreshold(
  level: SensitivityLevel,
  threshold: SensitivityLevel
): boolean {
  return SENSITIVITY_LEVEL_ORDER.indexOf(level) >= SENSITIVITY_LEVEL_ORDER.indexOf(threshold);
}

interface ContentRule {
  id: string;
  pattern: RegExp;
}

// Deliberately conservative, high-signal patterns — this is a fallback net,
// not the primary control (the label is). Never store/log the matched text,
// only the rule id.
const CONTENT_RULES: ContentRule[] = [
  {
    id: "sensitivity-keyword",
    pattern:
      /\b(confidentiel(le)?s?|confidential|strictement confidentiel|secret(s)?|ne pas diffuser|do not distribute|non[- ]disclosure agreement|\bnda\b)\b/i,
  },
  {
    id: "credit-card-number",
    pattern:
      /\b(?:4[0-9]{12}(?:[0-9]{3})?|5[1-5][0-9]{14}|3[47][0-9]{13}|6(?:011|5[0-9]{2})[0-9]{12})\b/,
  },
  { id: "iban", pattern: /\b[A-Z]{2}\d{2}[A-Z0-9]{11,30}\b/ },
  {
    id: "cloud-api-key",
    pattern:
      /\b(sk-[A-Za-z0-9]{16,}|AKIA[0-9A-Z]{16}|ghp_[A-Za-z0-9]{30,}|xox[baprs]-[A-Za-z0-9-]{10,})\b/,
  },
  {
    id: "generic-secret-assignment",
    pattern:
      /\b(api[_-]?key|secret[_-]?key|password|passwd|access[_-]?token)\s*[:=]\s*['"]?\S{8,}/i,
  },
];

/**
 * Client-side content heuristic — the fallback gate used only when a
 * sensitivity label cannot be resolved. Returns the first matching rule's id,
 * never the matched substring, so a caller can report the hit without
 * leaking the sensitive value into logs or the UI.
 */
export function scanForSensitiveContent(text: string): ContentScanResult {
  for (const rule of CONTENT_RULES) {
    if (rule.pattern.test(text)) {
      return { hit: true, ruleId: rule.id };
    }
  }
  return { hit: false };
}

/**
 * Generic, non-leaking user-facing message for a block — never includes the
 * label name or the matched heuristic rule, only which of the two blocked it.
 */
export function classificationBlockMessage(error: BlockedByClassificationError): string {
  return error.reason === "label"
    ? "This email is classified and cannot be processed by the AI assistant."
    : "This email's content matches a sensitive-data pattern and cannot be sent to the AI assistant.";
}

/**
 * Single gate called at every point an email enters the AI pipeline
 * (insights.ts, triage.ts, agent-loop.ts). Throws `BlockedByClassificationError`
 * when the email must not be sent to the AI endpoint; resolves (void)
 * otherwise. When `settings.purviewFilterEnabled` is false this is a no-op —
 * behavior is byte-for-byte identical to before this feature existed.
 */
export async function assertSafeToSendToAI(
  itemId: string | null,
  subject: string,
  body: string,
  adapter: Pick<MailboxAdapter, "getSensitivityLabel">,
  settings: AddinSettings,
  policy: OrgPolicy = {}
): Promise<void> {
  if (!settings.purviewFilterEnabled) {
    return;
  }

  const threshold = policy.blockThreshold ?? DEFAULT_BLOCK_THRESHOLD;
  // No resolvable item id (e.g. a REST-id conversion failure) means the label
  // can never be read — treat exactly like "no label found" (unknown).
  const labelName = itemId ? await adapter.getSensitivityLabel(itemId) : null;
  const classification = classifyLabel(labelName, policy.sensitivityLabelMap ?? {});

  if (classification.level !== "unknown") {
    if (isAtOrAboveThreshold(classification.level, threshold)) {
      throw new BlockedByClassificationError("label", classification.labelName);
    }
    return;
  }

  const scan = scanForSensitiveContent(`${subject}\n${body}`);
  if (scan.hit) {
    throw new BlockedByClassificationError("content-heuristic", scan.ruleId);
  }
}

const REDACTED_SUBJECT = "[subject withheld — classified content]";
const REDACTED_PREVIEW = "[preview withheld — classified content]";

/**
 * Redacts the `subject` and `preview` fields of any email summary whose
 * sensitivity classification disallows AI processing, replacing both with a
 * fixed placeholder — the id/metadata are kept (the caller, e.g. a listing
 * tool, still needs them to be useful), but neither the subject nor the
 * preview text ever reaches the model. Subject is redacted too, not just
 * preview: it's the field most callers (list/search results, the day-summary
 * digest) actually surface, and it's also what the content heuristic scans.
 * No-op when the Purview filter is off. Shared by every call site that hands
 * a *list* of email summaries to the AI (agent-loop tool results, the
 * day-summary digest) — as opposed to `assertSafeToSendToAI`, used where a
 * single email's full content is at stake.
 */
export async function redactSensitiveSummaries(
  items: EmailSummary[],
  adapter: Pick<MailboxAdapter, "getSensitivityLabel">,
  settings: AddinSettings,
  policy: OrgPolicy = {}
): Promise<EmailSummary[]> {
  if (!settings.purviewFilterEnabled) {
    return items;
  }
  return Promise.all(
    items.map(async (item) => {
      try {
        await assertSafeToSendToAI(item.id, item.subject, item.preview, adapter, settings, policy);
        return item;
      } catch (error) {
        if (error instanceof BlockedByClassificationError) {
          return { ...item, subject: REDACTED_SUBJECT, preview: REDACTED_PREVIEW };
        }
        throw error;
      }
    })
  );
}
